import { useEffect, useMemo, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { usernameSchema } from '@garagegymbros/contracts';

export type ProfileNameAvailability = {
  name: string;
  status: 'idle' | 'invalid' | 'checking' | 'available' | 'taken' | 'error';
  message: string;
};
export type ProfileNameLookup = (name: string) => Promise<boolean>;

/** Preserve only the signed-in member's exact preexisting, server-generated name. */
export function isUnchangedLegacyProfileName(raw: string, currentName: string, userId: string) {
  return (
    raw === currentName &&
    /^member_[a-f0-9]{32}$/.test(raw) &&
    raw === `member_${userId.replace(/-/g, '').toLowerCase()}`
  );
}

export async function lookupProfileName(client: SupabaseClient, name: string): Promise<boolean> {
  const { data, error } = await client.rpc('profile_name_available', { profile_name: name });
  if (error || typeof data !== 'boolean')
    throw new Error('Profile name availability could not be checked. Please try again.');
  return data;
}

export function initialProfileNameAvailability(raw: string): ProfileNameAvailability {
  if (!raw.trim()) return { name: '', status: 'idle', message: '' };
  const parsed = usernameSchema.safeParse(raw);
  return parsed.success
    ? { name: parsed.data, status: 'checking', message: 'Checking profile name…' }
    : { name: raw, status: 'invalid', message: parsed.error.issues[0].message };
}

/** A later input or unmount invalidates in-flight results, including failures. */
export function createProfileNameAvailabilityCheck() {
  let revision = 0;
  return {
    cancel() {
      revision += 1;
    },
    async check(raw: string, lookup: ProfileNameLookup): Promise<ProfileNameAvailability | null> {
      const request = ++revision;
      const initial = initialProfileNameAvailability(raw);
      if (initial.status !== 'checking') return initial;
      try {
        const available = await lookup(initial.name);
        if (request !== revision) return null;
        return {
          name: initial.name,
          status: available ? 'available' : 'taken',
          message: available
            ? 'This profile name is available. It is confirmed when you save.'
            : 'That profile name is already taken. Choose another.',
        };
      } catch {
        if (request !== revision) return null;
        return {
          name: initial.name,
          status: 'error',
          message: 'Availability could not be checked. Try again before saving.',
        };
      }
    },
  };
}

export function useProfileNameAvailability(
  raw: string,
  lookup: ProfileNameLookup,
  enabled = true,
): ProfileNameAvailability {
  const checker = useMemo(createProfileNameAvailabilityCheck, []);
  const [result, setResult] = useState(() => initialProfileNameAvailability(raw));
  useEffect(() => {
    checker.cancel();
    const initial = initialProfileNameAvailability(raw);
    setResult(initial);
    if (!enabled || initial.status !== 'checking') return;
    const timeout = setTimeout(() => {
      void checker.check(raw, lookup).then((next) => {
        if (next) setResult(next);
      });
    }, 350);
    return () => {
      clearTimeout(timeout);
      checker.cancel();
    };
  }, [checker, raw, lookup, enabled]);
  // Never show the preceding input's availability while the next effect is pending.
  const initial = initialProfileNameAvailability(raw);
  return result.name === initial.name ? result : initial;
}
